import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    ChevronLeft, Calendar, Search, Plus, Clock,
    MoreVertical, Trash2, Edit3, X, Loader2, Users, Eye, Image as ImageIcon
} from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';

const ManageEvents = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [club, setClub] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [actionMenuOpen, setActionMenuOpen] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all', 'upcoming', 'past'

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        eventDate: '',
        eventTime: ''
    });

    const fetchClubDetails = useCallback(async () => {
        try {
            const data = await clubsService.getClub(clubId);
            setClub(data);
        } catch (err) {
            console.error('Failed to fetch club details:', err);
        }
    }, [clubId]);

    const fetchEvents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await clubsService.getClubAdminEvents(clubId);
            setEvents(data);
        } catch (err) {
            console.error(err);
            setError('Failed to load events. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        fetchClubDetails();
        fetchEvents();
    }, [fetchClubDetails, fetchEvents]);

    const isPremiumVenue = Boolean(club?.isPremiumVenue || (club?.profileTier || '').toLowerCase() === 'premium');
    const openUpgrade = () => navigate(`/dashboard/club/${clubId}/plan`);

    const handleCreateEvent = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!formData.name || !formData.eventDate) {
            setError('Event name and date are required');
            return;
        }

        setIsSubmitting(true);
        try {
            await clubsService.createClubAdminEvent(clubId, {
                ...formData,
                clubId: parseInt(clubId, 10)
            });
            await fetchEvents();
            setShowCreateModal(false);
            resetForm();
        } catch (err) {
            console.error('Failed to create event:', err);
            setError(err.message || 'Failed to create event. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateEvent = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!selectedEvent || !formData.name || !formData.eventDate) {
            setError('Event name and date are required');
            return;
        }

        setIsSubmitting(true);
        try {
            await clubsService.updateClubAdminEvent(clubId, selectedEvent.eventId, formData);
            await fetchEvents();
            setShowEditModal(false);
            setSelectedEvent(null);
            resetForm();
        } catch (err) {
            console.error('Failed to update event:', err);
            setError(err.message || 'Failed to update event. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteEvent = async (event) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!window.confirm(`Are you sure you want to delete "${event.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await clubsService.deleteClubAdminEvent(clubId, event.eventId);
            await fetchEvents();
        } catch (err) {
            console.error('Failed to delete event:', err);
            setError(err.message || 'Failed to delete event. Please try again.');
        }
        setActionMenuOpen(null);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            eventDate: '',
            eventTime: ''
        });
    };

    const openEditModal = (event) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setSelectedEvent(event);
        setFormData({
            name: event.name || '',
            description: event.description || '',
            eventDate: event.eventDate ? event.eventDate.split('T')[0] : '',
            eventTime: event.eventTime || ''
        });
        setShowEditModal(true);
        setActionMenuOpen(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No date';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
            weekday: 'short',
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        // Handle both "HH:mm:ss" and "HH:mm" formats
        const parts = timeString.split(':');
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
        }
        return timeString;
    };

    const isUpcoming = (dateString) => {
        if (!dateString) return false;
        return new Date(dateString) >= new Date();
    };

    const filteredEvents = events.filter(event => {
        const matchesSearch = event.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (filter === 'upcoming') {
            return matchesSearch && isUpcoming(event.eventDate);
        } else if (filter === 'past') {
            return matchesSearch && !isUpcoming(event.eventDate);
        }
        return matchesSearch;
    });

    // Sort events by date
    const sortedEvents = [...filteredEvents].sort((a, b) => {
        const dateA = new Date(a.eventDate || 0);
        const dateB = new Date(b.eventDate || 0);
        return filter === 'past' ? dateB - dateA : dateA - dateB;
    });

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading events...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            {/* Back Button */}
            <button 
                className="club-dashboard-back"
                onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}
            >
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={club} clubId={clubId} navigate={navigate} premiumOnlyFeature featureLabel="Events" />

            {/* Header */}
            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-orange">
                        <Calendar size={24} />
                    </div>
                    <div>
                        <h1>Manage Events</h1>
                        <p>{events.length} event{events.length !== 1 ? 's' : ''} at {club?.name}</p>
                    </div>
                </div>
                <button 
                    className="manage-section-add-btn"
                    onClick={() => setShowCreateModal(true)}
                    disabled={!isPremiumVenue}
                >
                    <Plus size={18} />
                    {isPremiumVenue ? 'Create Event' : 'Upgrade For Events'}
                </button>
            </div>

            {!isPremiumVenue ? (
                <div className="venue-premium-lock-note">
                    Events are a Premium venue feature. Free venues can view this area, but creating and editing events is unlocked from the Venue Plan page.
                </div>
            ) : null}

            {error && (
                <div className="manage-clubs-error">
                    {error}
                    <button onClick={() => setError(null)} className="error-dismiss">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Toolbar */}
            <div className="manage-section-toolbar">
                <div className="manage-clubs-search">
                    <Search size={18} className="manage-clubs-search-icon" />
                    <input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-tabs">
                    <button 
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All
                    </button>
                    <button 
                        className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setFilter('upcoming')}
                    >
                        Upcoming
                    </button>
                    <button 
                        className={`filter-tab ${filter === 'past' ? 'active' : ''}`}
                        onClick={() => setFilter('past')}
                    >
                        Past
                    </button>
                </div>
            </div>

            {/* Events List */}
            {sortedEvents.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Calendar size={48} />
                    </div>
                    <h3>No Events Found</h3>
                    <p>{searchTerm ? 'Try a different search term' : isPremiumVenue ? 'Create your first event to get started' : 'Upgrade to Premium to start publishing venue events'}</p>
                </div>
            ) : (
                <div className="events-list">
                    {sortedEvents.map((event) => (
                        <div key={event.eventId} className={`event-card ${!isUpcoming(event.eventDate) ? 'past' : ''}`}>
                            <div className="event-date-badge">
                                <span className="event-date-day">{new Date(event.eventDate).getDate()}</span>
                                <span className="event-date-month">{new Date(event.eventDate).toLocaleDateString('en-GB', { month: 'short' })}</span>
                            </div>
                            {event.coverImageUrl && (
                                <div className="event-card-cover">
                                    <img src={event.coverImageUrl} alt={`${event.name} cover`} />
                                </div>
                            )}
                            <div className="event-info">
                                <h4>{event.name}</h4>
                                <div className="event-meta">
                                    <span className="event-meta-item">
                                        <Calendar size={14} />
                                        {formatDate(event.eventDate)}
                                    </span>
                                    {event.eventTime && (
                                        <span className="event-meta-item">
                                            <Clock size={14} />
                                            {formatTime(event.eventTime)}
                                        </span>
                                    )}
                                    {event.shooterCount > 0 && (
                                        <span className="event-meta-item">
                                            <Users size={14} />
                                            {event.shooterCount} shooter{event.shooterCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {event.imageCount > 0 && (
                                        <span className="event-meta-item">
                                            <ImageIcon size={14} />
                                            {event.imageCount} image{event.imageCount === 1 ? '' : 's'}
                                        </span>
                                    )}
                                </div>
                                {event.description && (
                                    <p className="event-description">{event.description}</p>
                                )}
                            </div>
                            <div className="event-actions">
                                <button 
                                    className="event-view-btn"
                                    onClick={() => navigate(`/dashboard/score?eventId=${event.eventId}`)}
                                    title="View Event"
                                >
                                    <Eye size={18} />
                                </button>
                                <button 
                                    className="member-action-btn"
                                    onClick={() => setActionMenuOpen(actionMenuOpen === event.eventId ? null : event.eventId)}
                                >
                                    <MoreVertical size={18} />
                                </button>
                                {actionMenuOpen === event.eventId && (
                                    <div className="member-action-menu">
                                        <button onClick={() => openEditModal(event)}>
                                            <Edit3 size={16} />
                                            Edit Event
                                        </button>
                                        <button 
                                            className="danger"
                                            onClick={() => handleDeleteEvent(event)}
                                        >
                                            <Trash2 size={16} />
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Event Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create Event</h2>
                            <button className="modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Event Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Sunday Morning Sporting"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Date *</label>
                                    <input
                                        type="date"
                                        value={formData.eventDate}
                                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Time</label>
                                    <input
                                        type="time"
                                        value={formData.eventTime}
                                        onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    placeholder="Add event details..."
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={4}
                                />
                            </div>
                            <div className="form-group">
                                <label>Event Images</label>
                                <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                                    Create the event first, then reopen it to upload and reorder event images.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => { setShowCreateModal(false); resetForm(); }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={handleCreateEvent}
                                disabled={!formData.name || !formData.eventDate || isSubmitting}
                            >
                                {isSubmitting ? <Loader2 size={18} className="btn-spinner" /> : null}
                                Create Event
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Event Modal */}
            {showEditModal && selectedEvent && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Edit Event</h2>
                            <button className="modal-close" onClick={() => { setShowEditModal(false); setSelectedEvent(null); resetForm(); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Event Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Sunday Morning Sporting"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Date *</label>
                                    <input
                                        type="date"
                                        value={formData.eventDate}
                                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Time</label>
                                    <input
                                        type="time"
                                        value={formData.eventTime}
                                        onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    placeholder="Add event details..."
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={4}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => { setShowEditModal(false); setSelectedEvent(null); resetForm(); }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={handleUpdateEvent}
                                disabled={!formData.name || !formData.eventDate || isSubmitting}
                            >
                                {isSubmitting ? <Loader2 size={18} className="btn-spinner" /> : null}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageEvents;
